import { useCallback, useEffect, useRef, useState } from 'react';

import type { TicketDetail } from '@nightshift/ui-types';

import { Spinner, StateChip } from '../../components/primitives.tsx';
import { api, ApiError, type SessionUser } from '../../live/api.ts';
import { useLive, useStore } from '../../live/context.tsx';
import { useTimeFormat } from '../../lib/display.ts';
import { absoluteTime, observedTime } from '../../lib/format.ts';
import {
  DRAWER_ACTION_LABEL,
  drawerActionsFor,
  gitlabMrHref,
  type DrawerAction,
} from '../../lib/drawer-actions.ts';
import {
  INSPECTOR_NARROW_MQ,
  MAX_INSPECTOR_WIDTH,
  MIN_INSPECTOR_WIDTH,
  clampInspectorWidth,
  inspectorFillsViewport,
  inspectorPointerX,
  inspectorViewportWidth,
  inspectorFullPage,
  inspectorWidth,
  inspectorWidthFromKey,
  inspectorWidthFromPointer,
  setInspectorFullPage,
  setInspectorWidth,
} from '../../lib/inspector.ts';
import { evidenceBadgeCount } from '../../lib/evidence-view.ts';
import { shortcutBlocked } from '../../lib/keys.ts';
import { dependencyBlockOf, type DependencyBlockPayload } from '../../lib/dependency-block.ts';
import { isTicketDraftConflict, reconcileTicketDraft, ticketDraftSnapshot } from '../../lib/ticket-draft.ts';
import { StartGatePanel } from './start-gate.tsx';
import { preflightIssuesOf, type PreflightIssue } from '../../lib/start-preflight.ts';
import { StartPreflightPanel } from './start-preflight.tsx';
import { canDeploy } from '../../lib/privilege.ts';
import { retryPhrase } from '../../lib/liveness.ts';
import { BriefTab } from './brief.tsx';
import { CommentsTab } from './comments.tsx';
import { DraftConflictWarning } from './draft-conflict.tsx';
import { DependenciesTab } from './dependencies.tsx';
import { DependencyPanel } from './dependency-panel.tsx';
import { dependencyPreviewPoke } from '../../lib/dependency-preview.ts';
import { DependencyPreview } from './dependency-preview.tsx';
import { EvidenceTab } from './evidence.tsx';
import { OverridesTab } from './overrides.tsx';
import { ReviewTab } from './review.tsx';
import { ReviewMergePanel } from './review-merge.tsx';
import { ObsoleteProposalPanel } from './obsolete-proposal.tsx';
import { ReviewPlanPanel } from './review-plan.tsx';
import { RunTranscript } from './run-transcript.tsx';
import { TimelineTab } from './timeline.tsx';
import { WaitingPanel } from './waiting.tsx';
import { PolicyApprovalBanner } from './policy-approval.tsx';
import { BaselineUpdatePanel } from './baselines.tsx';
import { MarkdownComposer } from '../../components/markdown-composer.tsx';
import './drawer.css';

/**
 * Dark Factory inspector. Tabs: Activity (timeline), Comments, Brief, Dependencies,
 * Evidence, Review, Overrides. Waiting is a header panel, not a tab. URL-addressable via
 * ?ticket= and ?drawer=. Not a full-page replacement of the board.
 */
const TABS = [
  'Activity',
  'Comments',
  'Brief',
  'Dependencies',
  'Evidence',
  'Review',
  'Overrides',
] as const;
type Tab = (typeof TABS)[number];

const LEGACY_TABS: Readonly<Record<string, Tab>> = {
  Timeline: 'Activity',
  Conversation: 'Comments',
  Criteria: 'Brief',
  Audit: 'Activity',
  Trace: 'Activity',
  Runs: 'Activity',
  Raw: 'Activity',
};

export interface DrawerProps {
  ticketKey: string;
  initialRun: number | null;
  selectedTab: string | null;
  onSelectTab: (tab: string | null) => void;
  onClose: () => void;
  user: SessionUser | null;
}

function isTab(value: string | null | undefined): value is Tab {
  return value !== null && value !== undefined && (TABS as readonly string[]).includes(value);
}

function resolveTab(value: string | null | undefined): Tab | null {
  if (isTab(value)) return value;
  return value ? (LEGACY_TABS[value] ?? null) : null;
}

function repairLabel(detail: TicketDetail): string | null {
  let used = 0;
  let max = 5;
  for (const row of detail.ticket.repairs) {
    if (row.used > used) {
      used = row.used;
      max = row.max;
    }
  }
  if (used === 0) return `${used}/${max}`;
  return `${used}/${max}`;
}

export function TicketDrawer({
  ticketKey,
  initialRun,
  selectedTab,
  onSelectTab,
  onClose,
  user,
}: DrawerProps) {
  const state = useLive();
  const tab: Tab = resolveTab(selectedTab) ?? 'Activity';
  const setTab = (next: Tab) => onSelectTab(next);
  // Bumped by Answer, so the Comments tab brings the question and the reply box into view even
  // when it is already the open tab.
  const [answerRequest, setAnswerRequest] = useState(0);
  const openAnswer = () => {
    setTab('Comments');
    setAnswerRequest((count) => count + 1);
  };
  const [detail, setDetail] = useState<TicketDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [width, setWidth] = useState(inspectorWidth);
  const [fullPage, setFullPage] = useState(inspectorFullPage);
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(INSPECTOR_NARROW_MQ).matches,
  );
  const drawerRef = useRef<HTMLElement>(null);
  const previewRef = useRef<HTMLElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const dragging = useRef(false);
  const fills = inspectorFillsViewport(fullPage, narrow);
  const poke = `${state.board?.generatedAt ?? 0}:${state.connection}`;
  // The preview refetches only when its own ticket changed, not on every board update.
  const previewPoke = previewKey ? dependencyPreviewPoke(state.board, previewKey, poke) : poke;
  const closePreview = useCallback(() => setPreviewKey(null), []);

  // Only the newest request may land. A live-update refresh for the previous ticket (or
  // scope) finishing after this one put that ticket's detail, and its action buttons, under
  // this key.
  const requestRef = useRef(0);
  const load = useCallback(async () => {
    const request = ++requestRef.current;
    try {
      const next = await api.ticket(ticketKey, state.projectSlug ?? undefined);
      if (request !== requestRef.current) return;
      setDetail(next);
      setError(null);
    } catch (caught) {
      if (request !== requestRef.current) return;
      if (caught instanceof ApiError && caught.status === 401) {
        setError('Your session expired. Sign in again to read this ticket.');
      } else {
        setError(caught instanceof Error ? caught.message : String(caught));
      }
    }
  }, [ticketKey, state.projectSlug]);

  useEffect(() => {
    setDetail(null);
    setError(null);
    setPreviewKey(null);
    void load();
    return () => {
      requestRef.current += 1;
    };
  }, [ticketKey, load]);

  useEffect(() => {
    if (detail) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poke]);

  useEffect(() => {
    const media = window.matchMedia(INSPECTOR_NARROW_MQ);
    const onChange = () => setNarrow(media.matches);
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const onResize = () => setWidth((current) => clampInspectorWidth(current, inspectorViewportWidth()));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const applyWidth = useCallback((next: number) => {
    const clamped = setInspectorWidth(next, undefined, inspectorViewportWidth());
    setWidth(clamped);
  }, []);

  const toggleFullPage = useCallback(() => {
    setFullPage((current) => {
      const next = !current;
      setInspectorFullPage(next);
      return next;
    });
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (shortcutBlocked(event)) return;
      if ((event.key === 'f' || event.key === 'F') && !narrow) {
        event.preventDefault();
        toggleFullPage();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [narrow, toggleFullPage]);

  useEffect(() => {
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const onKey = (event: KeyboardEvent) => {
      const drawer = drawerRef.current;
      const preview = previewRef.current;
      // Focus on the page body while the preview is open is the preview's own doing -- a
      // refresh that removed the focused element -- so Escape there closes the preview, not
      // the whole inspector, which is what the shell's handler would otherwise do.
      if (preview && event.key === 'Escape' && isPageBody(event.target)) {
        event.preventDefault();
        event.stopPropagation();
        closePreview();
        return;
      }
      if (
        !drawer
        || (!drawer.contains(event.target as Node) && !preview?.contains(event.target as Node))
      ) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        if (preview) closePreview();
        else onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [drawer, ...(preview ? [preview] : [])].flatMap((pane) =>
        Array.from(pane.querySelectorAll<HTMLElement>(
          'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
        )),
      );
      if (focusable.length === 0) {
        event.preventDefault();
        drawer.focus();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey, true);
    const first = drawerRef.current?.querySelector<HTMLElement>(
      'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
    );
    first?.focus();
    return () => {
      window.removeEventListener('keydown', onKey, true);
      if (openerRef.current?.isConnected) openerRef.current.focus();
    };
  }, [closePreview, onClose]);

  const staleLive = state.connection !== 'live';
  const drawerClass = [
    'drawer',
    fills ? 'is-full' : '',
    narrow ? 'is-narrow' : '',
    previewKey ? 'has-dependency-preview' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const isAdmin = canDeploy(user);
  const actions = detail
    ? drawerActionsFor(detail.ticket.state, {
        waiting: detail.waiting,
        isAdmin,
        mrIid: detail.ticket.mr?.iid ?? null,
        deployConfigured: detail.deployConfigured,
        deployable: detail.deployable,
      })
    : [];

  return (
    <>
      {fills ? null : <div className="drawer-scrim" onClick={onClose} role="presentation" />}
      <aside
        ref={drawerRef}
        className={drawerClass}
        role="dialog"
        aria-modal={previewKey === null}
        aria-label={`${ticketKey} detail`}
        style={fills ? undefined : { width: `${width}px` }}
      >
        {fills ? null : (
          <div
            className="drawer-resize"
            role="separator"
            tabIndex={0}
            aria-orientation="vertical"
            aria-label="Inspector width"
            aria-valuemin={MIN_INSPECTOR_WIDTH}
            aria-valuemax={MAX_INSPECTOR_WIDTH}
            aria-valuenow={width}
            title="Drag or use arrow keys to resize"
            onPointerDown={(event) => {
              dragging.current = true;
              event.currentTarget.setPointerCapture(event.pointerId);
            }}
            onPointerMove={(event) => {
              if (!dragging.current) return;
              setWidth(inspectorWidthFromPointer(inspectorViewportWidth(), inspectorPointerX(event.clientX)));
            }}
            onPointerUp={(event) => {
              if (!dragging.current) return;
              dragging.current = false;
              applyWidth(inspectorWidthFromPointer(inspectorViewportWidth(), inspectorPointerX(event.clientX)));
            }}
            onPointerCancel={() => {
              dragging.current = false;
            }}
            onKeyDown={(event) => {
              const nextWidth = inspectorWidthFromKey(width, event.key, inspectorViewportWidth());
              if (nextWidth === null) return;
              event.preventDefault();
              applyWidth(nextWidth);
            }}
          />
        )}
        <header className="drawer-head">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--s3)' }}>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 7 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span className="mono" style={{ fontSize: 'var(--fs-mid)', color: 'var(--text-2)' }}>
                  {ticketKey}
                </span>
                {detail ? <StateChip state={detail.ticket.state} size="md" /> : null}
                {detail?.ticket.activity ? (
                  <span className="chip" style={{ fontSize: 'var(--fs-micro)' }}>
                    {detail.ticket.activity}
                  </span>
                ) : null}
                {staleLive ? (
                  <span className="drawer-stale" role="status">
                    Live connection is down{retryPhrase(state.reconnectInSeconds)}
                  </span>
                ) : null}
              </div>
              {detail ? (
                <TicketIdentity detail={detail} onReloaded={load} />
              ) : (
                <h2 style={{ fontSize: 'var(--fs-large)', lineHeight: 1.25 }}>Loading…</h2>
              )}
            </div>
            <div className="drawer-tools">
              {narrow ? null : (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={toggleFullPage}
                  aria-pressed={fullPage}
                  title={fullPage ? 'Dock to the side (F)' : 'Full page (F)'}
                >
                  {fullPage ? 'Dock' : 'Full page'}
                </button>
              )}
              <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close" title="Close (Esc)">
                ✕
              </button>
            </div>
          </div>

          {detail ? (
            <DependencyPanel
              detail={detail}
              now={state.tick}
              onOpenTab={() => setTab('Dependencies')}
            />
          ) : null}

          {detail?.waiting ? (
            <TicketActions
              detail={detail}
              actions={actions}
              onDone={() => void load()}
              onOpenComments={openAnswer}
            />
          ) : detail ? (
            <TicketActions detail={detail} actions={actions} onDone={() => void load()} />
          ) : null}

          {detail && detail.deployments.length > 0 ? (
            <ol className="drawer-deploys" aria-label="Deployment history">
              {detail.deployments.map((row) => (
                <li key={row.id}>
                  <span className="mono">{row.environment}</span>
                  {' · '}
                  {row.status}
                  {row.healthStatus === 'passed'
                    ? ' · healthy'
                    : row.healthStatus === 'failed'
                      ? ' · health failed'
                      : row.healthStatus === 'pending' && row.status === 'succeeded'
                        ? ' · checking'
                        : ''}
                  {' · '}
                  <span className="mono">{row.sha.slice(0, 12)}</span>
                  {row.jobUrl ? (
                    <>
                      {' · '}
                      <a href={row.jobUrl} target="_blank" rel="noreferrer">
                        job
                      </a>
                    </>
                  ) : null}
                </li>
              ))}
            </ol>
          ) : null}

          <div className="drawer-tabs" role="tablist">
            {TABS.map((name) => (
              <button
                key={name}
                type="button"
                role="tab"
                className="drawer-tab"
                aria-selected={tab === name}
                onClick={() => setTab(name)}
              >
                {name}
                {name === 'Activity' && detail ? (
                  <span className="dim num" style={{ marginLeft: 5, fontSize: 'var(--fs-micro)' }}>
                    {detail.timeline.length}
                  </span>
                ) : null}
                {name === 'Comments' && detail && detail.comments.length > 0 ? (
                  <span className="dim num" style={{ marginLeft: 5, fontSize: 'var(--fs-micro)' }}>
                    {detail.comments.length}
                  </span>
                ) : null}
                {/*
                  Live rows only. Counting stale and superseded artifacts reported the size of
                  the table rather than the size of the reviewer's job: CELL-28 wore a 349 whose
                  useful content was 153 rows.
                */}
                {name === 'Evidence' && detail && evidenceBadgeCount(detail) > 0 ? (
                  <span className="dim num" style={{ marginLeft: 5, fontSize: 'var(--fs-micro)' }}>
                    {evidenceBadgeCount(detail)}
                  </span>
                ) : null}
                {/* Only unmet blockers earn a number here: a satisfied dependency is history. */}
                {name === 'Dependencies' && detail && detail.dependencies.unmetCount > 0 ? (
                  <span className="dim num" style={{ marginLeft: 5, fontSize: 'var(--fs-micro)' }}>
                    {detail.dependencies.unmetCount}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        </header>

        <div className="drawer-body scroll-y">
          {error && detail ? (
            <p className="drawer-refresh-warning" role="alert">
              Ticket refresh failed. The loaded editor and any local changes are preserved. {error}
            </p>
          ) : null}
          {!detail && error ? (
            <p style={{ color: 'var(--danger-text)' }} role="alert">{error}</p>
          ) : !detail ? (
            <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--s8)' }}>
              <Spinner size={18} />
            </div>
          ) : tab === 'Activity' ? (
            <>
              {initialRun !== null ? <RunTranscript runId={initialRun} /> : null}
              <TimelineTab entries={detail.timeline} now={state.tick} effort={detail.effort} routing={detail.routing} />
            </>
          ) : tab === 'Comments' ? (
            <CommentsTab
              detail={detail}
              now={state.tick}
              canAnswer={detail.waiting?.reasonType === 'human_clarification'}
              answerRequest={answerRequest}
              onReloaded={load}
            />
          ) : tab === 'Brief' ? (
            <BriefTab detail={detail} now={state.tick} onReloaded={load} />
          ) : tab === 'Dependencies' ? (
            <DependenciesTab
              detail={detail}
              now={state.tick}
              onReloaded={load}
              onPreviewTicket={setPreviewKey}
            />
          ) : tab === 'Evidence' ? (
            <EvidenceTab detail={detail} now={state.tick} onReloaded={load} />
          ) : tab === 'Review' ? (
            <ReviewTab detail={detail} now={state.tick} />
          ) : (
            <OverridesTab detail={detail} onReloaded={load} />
          )}
        </div>
      </aside>
      {previewKey ? (
        <DependencyPreview
          previewRef={previewRef}
          ticketKey={previewKey}
          projectSlug={state.projectSlug}
          poke={previewPoke}
          onClose={closePreview}
        />
      ) : null}
    </>
  );
}

function TicketIdentity({
  detail,
  onReloaded,
}: {
  detail: TicketDetail;
  onReloaded: () => Promise<void>;
}) {
  const state = useLive();
  const store = useStore();
  const format = useTimeFormat();
  const [title, setTitle] = useState(detail.ticket.title);
  const [milestoneId, setMilestoneId] = useState(detail.ticket.milestoneId);
  const [milestoneDirty, setMilestoneDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(false);
  const ticketKeyRef = useRef(detail.ticket.ticketKey);
  const draftVersionRef = useRef(detail.ticket.version);
  const draftFieldsRef = useRef({
    title: detail.ticket.title,
    milestoneId: detail.ticket.milestoneId,
  });
  const dirtyFieldsRef = useRef({ title: false, milestoneId: false });
  const dirtyRef = useRef(false);

  function markDirty(field: keyof typeof dirtyFieldsRef.current) {
    if (!dirtyRef.current) {
      draftVersionRef.current = detail.ticket.version;
      draftFieldsRef.current = {
        title: detail.ticket.title,
        milestoneId: detail.ticket.milestoneId,
      };
    }
    dirtyFieldsRef.current[field] = true;
    dirtyRef.current = true;
  }

  useEffect(() => {
    const refresh = reconcileTicketDraft({
      dirty: dirtyRef.current,
      draftTicketKey: ticketKeyRef.current,
      draftVersion: draftVersionRef.current,
      incomingTicketKey: detail.ticket.ticketKey,
      incomingVersion: detail.ticket.version,
      draftSnapshot: ticketDraftSnapshot(
        draftFieldsRef.current.title,
        draftFieldsRef.current.milestoneId,
      ),
      incomingSnapshot: ticketDraftSnapshot(detail.ticket.title, detail.ticket.milestoneId),
    });
    ticketKeyRef.current = detail.ticket.ticketKey;
    if (refresh !== 'reset') {
      if (refresh === 'conflict') setConflict(true);
      return;
    }
    dirtyRef.current = false;
    dirtyFieldsRef.current = { title: false, milestoneId: false };
    draftVersionRef.current = detail.ticket.version;
    draftFieldsRef.current = {
      title: detail.ticket.title,
      milestoneId: detail.ticket.milestoneId,
    };
    setConflict(false);
    setTitle(detail.ticket.title);
    setMilestoneId(detail.ticket.milestoneId);
    setMilestoneDirty(false);
  }, [detail.ticket.ticketKey, detail.ticket.version, detail.ticket.title, detail.ticket.milestoneId]);

  async function save(patch: { title?: string; milestoneId?: number | null }) {
    if (conflict) return;
    setBusy(true);
    try {
      await api.updateTicket(detail.ticket.ticketKey, {
        ...patch,
        expectedVersion: draftVersionRef.current,
        expectedFields: draftFieldsRef.current,
      });
      if (patch.title !== undefined) {
        draftFieldsRef.current.title = patch.title;
        dirtyFieldsRef.current.title = false;
      }
      if (Object.prototype.hasOwnProperty.call(patch, 'milestoneId')) {
        draftFieldsRef.current.milestoneId = patch.milestoneId ?? null;
        dirtyFieldsRef.current.milestoneId = false;
        setMilestoneDirty(false);
      }
      dirtyRef.current = dirtyFieldsRef.current.title || dirtyFieldsRef.current.milestoneId;
      setConflict(false);
      await onReloaded();
    } catch (error) {
      if (isTicketDraftConflict(error)) setConflict(true);
      store.toast('bad', error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  const facts = [
    observedTime(detail.ticket.updatedAt, state.tick, format),
    `repair ${repairLabel(detail)}`,
    detail.ticket.agent,
    detail.ticket.machine,
    detail.ticket.mr ? `MR !${detail.ticket.mr.iid}` : null,
  ].filter((bit): bit is string => Boolean(bit));

  /*
   * One line saying what is going on, above the actions.
   *
   * A stall outranks landing progress: if the detector has decided nothing is happening, the
   * loop's last cheerful "waiting for CI" is the thing being contradicted, not the headline.
   *
   * There is deliberately no third fallback. `inspectorNext` used to write a generic sentence
   * per state ("Approved. Rebasing, waiting for CI, and merging") and was never rendered
   * anywhere; it has been deleted. A state chip already says the state, and this slot is for
   * the one thing the chip cannot say -- what is actually happening right now.
   */
  const notice = detail.ticket.stalled
    ? { tone: 'warn' as const, text: detail.ticket.stalled }
    : detail.ticket.landing
      ? { tone: 'info' as const, text: detail.ticket.landing.detail }
      : null;

  /**
   * The generated display title, when the `ticket-display` sidecar has a current result. It is
   * what the board card prints, so showing it here stops the drawer reverting to the original
   * wording. It never replaces the stored title: the editable input below still holds
   * `ticket.title` and is still what a save writes.
   */
  const generated = detail.ticket.displayTitle ?? null;

  return (
    <>
      {conflict ? <DraftConflictWarning field="title" /> : null}
      {generated ? (
        <h2 className="drawer-display-title" title={`Original title: ${detail.ticket.title}`}>
          {generated}
        </h2>
      ) : null}
      <h2 style={{ fontSize: 'var(--fs-large)', lineHeight: 1.25 }}>
        {generated ? <span className="drawer-section-kicker drawer-stored-title-kicker">Stored title</span> : null}
        <input
          className={generated ? 'drawer-title-input drawer-title-input-secondary' : 'drawer-title-input'}
          value={title}
          aria-label="Ticket title"
          disabled={busy}
          onChange={(event) => {
            markDirty('title');
            setTitle(event.target.value);
          }}
          onBlur={() => {
            if (title.trim() === detail.ticket.title) {
              dirtyFieldsRef.current.title = false;
              dirtyRef.current = dirtyFieldsRef.current.milestoneId;
              setTitle(detail.ticket.title);
              return;
            }
            void save({ title });
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              (event.currentTarget as HTMLInputElement).blur();
            }
          }}
        />
      </h2>
      <div className="drawer-facts">
        <label className="drawer-milestone">
          <span className="visually-hidden">Milestone</span>
          <select
            className="input"
            aria-label="Milestone"
            disabled={busy}
            value={milestoneId == null ? '' : String(milestoneId)}
            onChange={(event) => {
              const raw = event.target.value;
              const nextMilestoneId = raw.length === 0 ? null : Number(raw);
              markDirty('milestoneId');
              setMilestoneId(nextMilestoneId);
              setMilestoneDirty(true);
              void save({ milestoneId: nextMilestoneId });
            }}
          >
            <option value="">No milestone</option>
            {state.milestones.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </select>
          {milestoneDirty ? (
            <button
              type="button"
              className="btn btn-sm"
              disabled={busy || conflict}
              onClick={() => void save({ milestoneId })}
            >
              Save milestone
            </button>
          ) : null}
        </label>
        {facts.length > 0 ? (
          <span className="mono dim" title={absoluteTime(detail.ticket.updatedAt)}>
            {facts.join(' · ')}
          </span>
        ) : null}
      </div>
      {notice ? (
        <p
          className="drawer-notice"
          role={notice.tone === 'warn' ? 'alert' : undefined}
          style={{
            margin: '0 0 10px',
            padding: '6px 10px',
            borderRadius: 'var(--r1)',
            fontSize: 'var(--fs-small)',
            background: notice.tone === 'warn' ? 'var(--warn-wash)' : 'var(--surface-2)',
            color: notice.tone === 'warn' ? 'var(--warn-text)' : 'var(--text-2)',
            boxShadow: notice.tone === 'warn' ? 'inset 0 0 0 1px var(--warn-edge)' : 'none',
          }}
        >
          {notice.text}
        </p>
      ) : null}
      <PolicyApprovalBanner detail={detail} />
    </>
  );
}

function TicketActions({
  detail,
  actions,
  onDone,
  onOpenComments,
}: {
  detail: TicketDetail;
  actions: DrawerAction[];
  onDone: () => void;
  onOpenComments?: () => void;
}) {
  const store = useStore();
  const live = useLive();
  const waiting = Boolean(detail.waiting);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<'idle' | DrawerAction>('idle');
  const [confirmKey, setConfirmKey] = useState('');
  const [childKey, setChildKey] = useState('');
  const [childTitle, setChildTitle] = useState('');
  const [sendNote, setSendNote] = useState('');
  const [resumeNote, setResumeNote] = useState('');
  const [sendTo, setSendTo] = useState<'Refining' | 'Implementing'>('Implementing');
  const [sendActivity, setSendActivity] = useState('');
  const [waitReasonType, setWaitReasonType] = useState('manual_hold');
  const [waitReason, setWaitReason] = useState('');
  const [blocked, setBlocked] = useState<DependencyBlockPayload | null>(null);
  const [refused, setRefused] = useState<PreflightIssue[] | null>(null);
  const key = detail.ticket.ticketKey;
  const version = detail.ticket.version;
  const gitlabHref = gitlabMrHref(detail.ticket.mr);

  async function run(label: string, action: () => Promise<unknown>) {
    setBusy(true);
    try {
      const result = await action();
      // A recovery command answers with a receipt: what changed and what happens next. It is
      // the sentence the person wants, so it replaces the generic label when present.
      const receipt = (result as { receipt?: { message?: string } } | null | undefined)?.receipt?.message;
      store.toast('good', receipt ?? label);
      setMode('idle');
      setConfirmKey('');
      onDone();
      await store.refetch();
    } catch (error) {
      store.toast('bad', `${label} failed — ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setBusy(false);
    }
  }

  /**
   * Start, with the server's dependency gate handled rather than reported as a failure.
   *
   * A blocked start is not an error the operator caused: it is the system saying "look at
   * this first". Showing it as a red toast would train people to ignore the toast, so the
   * refusal opens a panel with the three things they can actually do about it.
   */
  async function startTicket(body: {
    overrideDependencies?: boolean;
    acknowledge?: Array<{ id: number; input_revision: number }>;
  }): Promise<void> {
    setBusy(true);
    try {
      const result = await api.start(key, { expectedVersion: version, ...body });
      store.toast('good', result.state === 'Waiting' ? 'Queued for shared edits' : body.overrideDependencies ? 'Started past its dependencies' : 'Started');
      setBlocked(null);
      setRefused(null);
      onDone();
      await store.refetch();
    } catch (error) {
      const block = dependencyBlockOf(error);
      const issues = preflightIssuesOf(error);
      if (block) {
        setBlocked(block);
      } else if (issues) {
        setRefused(issues);
      } else {
        store.toast('bad', `Start failed — ${error instanceof Error ? error.message : String(error)}`);
      }
    } finally {
      setBusy(false);
    }
  }

  function fire(action: DrawerAction): void {
    if (
      action === 'cancel' ||
      action === 'merge' ||
      action === 'plan-review' ||
      action === 'obsolete-review' ||
      action === 'send-back' ||
      action === 'wait' ||
      action === 'split' ||
      action === 'reset-budget' ||
      action === 'approve-baseline'
    ) {
      setMode(mode === action ? 'idle' : action);
      return;
    }
    if (action === 'open-gitlab') return;
    if (action === 'answer') {
      onOpenComments?.();
      return;
    }
    if (action === 'approve') {
      void run('Approved — landing now', () => api.approve(key, version));
    }
    if (action === 'start') void startTicket({});
    if (action === 'record_done') {
      void run('Recorded as already done', () => api.recordDone(key, { expectedVersion: version }));
    }
    if (action === 'shelve') void run('Shelved', () => api.shelve(key, { expectedVersion: version }));
    if (action === 'restore') void run('Restored', () => api.restore(key, version));
    if (action === 'resume') {
      const prerequisite = detail.waiting?.resumePrerequisite;
      const override = prerequisite !== undefined && !prerequisite.ok;
      if (override && prerequisite.alternative === 'fix-environment') {
        setMode('resume');
        return;
      }
      void run(override ? 'Resumed anyway' : 'Resumed', () => api.resume(key, version, override));
    }
    if (action === 'refresh') void run('External state refreshed', () => api.refreshExternalState(key));
    if (action === 'evidence-repair') {
      void run('Evidence repair queued', () => api.evidenceRepair(key, version));
    }
    if (action === 'retry-verification') {
      void run('Verification job retried', () => api.retryVerification(key, version));
    }
    if (action === 'deploy') void run('Deploy started', () => api.deploy(key));
    if (action === 'plan-approve') {
      void run('Plan approved', () => api.planDecision(key, 'approve', { expectedVersion: version }));
    }
    if (action === 'plan-request-changes') {
      void run('Plan changes requested', () =>
        api.planDecision(key, 'request-changes', { expectedVersion: version }),
      );
    }
    if (action === 'plan-defer') {
      void run('Plan deferred', () => api.planDecision(key, 'defer', { expectedVersion: version }));
    }
    if (action === 'plan-reject') {
      void run('Plan rejected', () => api.planDecision(key, 'reject', { expectedVersion: version }));
    }
  }

  const barActions = waiting ? actions.filter((action) => action === 'reset-budget') : actions;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
      {detail.waiting ? (
        <WaitingPanel waiting={detail.waiting} actions={actions} busy={busy} onAction={fire} />
      ) : null}

      {refused ? <StartPreflightPanel issues={refused} onClose={() => setRefused(null)} /> : null}

      {blocked ? (
        <StartGatePanel
          ticketKey={key}
          block={blocked}
          busy={busy}
          onClose={() => setBlocked(null)}
          onChanged={async () => {
            onDone();
            await store.refetch();
          }}
          onStartAnyway={(acknowledge) =>
            void startTicket({ overrideDependencies: true, acknowledge })
          }
        />
      ) : null}

      {barActions.length > 0 ? (
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center' }}>
          {barActions.map((action) => {
            if (action === 'open-gitlab') {
              if (!gitlabHref) {
                return (
                  <button
                    key={action}
                    type="button"
                    className="btn btn-sm"
                    disabled
                    title="This ticket has a merge request iid but no GitLab URL"
                  >
                    {DRAWER_ACTION_LABEL[action]}
                  </button>
                );
              }
              return (
                <a
                  key={action}
                  className="btn btn-sm"
                  href={gitlabHref}
                  target="_blank"
                  rel="noreferrer"
                  style={{ textDecoration: 'none' }}
                >
                  {DRAWER_ACTION_LABEL[action]}
                </a>
              );
            }
            const destructive = action === 'cancel';
            return (
              <button
                key={action}
                type="button"
                className={
                  destructive
                    ? 'btn btn-danger btn-sm'
                    : action === 'start' || action === 'resume' || action === 'approve'
                      ? 'btn btn-accent'
                      : 'btn'
                }
                disabled={busy}
                style={destructive ? { marginLeft: 'auto' } : undefined}
                onClick={() => fire(action)}
              >
                {DRAWER_ACTION_LABEL[action]}
              </button>
            );
          })}
        </div>
      ) : null}

      {mode === 'merge' ? (
        <ReviewMergePanel detail={detail} now={live.tick} onCancel={() => setMode('idle')} />
      ) : null}

      {mode === 'approve-baseline' ? (
        <BaselineUpdatePanel
          detail={detail}
          busy={busy}
          onCancel={() => setMode('idle')}
          onApprove={(scenarios, note) => {
            void run('Baselines updated', () =>
              api.approveBaselines(key, { scenarios, ...(note ? { note } : {}), expectedVersion: version }),
            );
          }}
        />
      ) : null}

      {mode === 'plan-review' ? (
        <ReviewPlanPanel
          detail={detail}
          now={live.tick}
          busy={busy}
          onCancel={() => setMode('idle')}
          onDecision={(decision) => {
            void run(decision === 'approve' ? 'Plan approved' : 'Plan deferred', () =>
              api.planDecision(key, decision, { expectedVersion: version }),
            );
          }}
        />
      ) : null}

      {mode === 'obsolete-review' ? (
        <ObsoleteProposalPanel
          detail={detail}
          busy={busy}
          onClose={() => setMode('idle')}
          onCancelTicket={(reason) => {
            void run('Cancelled as obsolete', () => api.cancel(key, key, version, reason));
          }}
          onKeep={(note) => {
            void run('Kept — refining again', () => api.answer(key, note, version));
          }}
        />
      ) : null}

      {mode === 'resume' ? (
        <div className="rise" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label htmlFor="resume-override-note">Reason for resuming despite the environment blocker</label>
          <MarkdownComposer id="resume-override-note" value={resumeNote} rows={3} disabled={busy} project={detail.ticket.projectSlug} onChange={setResumeNote} placeholder="What changed or why is it safe to try again?" />
          <div style={{ display: 'flex', gap: 7 }}>
            <button type="button" className="btn" disabled={busy || !resumeNote.trim()} onClick={() => void run('Resumed with operator reason', () => api.resume(key, version, true, resumeNote))}>Resume anyway</button>
            <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => setMode('idle')}>Cancel</button>
          </div>
        </div>
      ) : null}

      {mode === 'send-back' ? (
        <div className="rise drawer-send-back" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label className="drawer-comment-label" htmlFor="send-back-note">
            Send-back note
          </label>
          <MarkdownComposer
            id="send-back-note"
            value={sendNote}
            rows={4}
            placeholder="What should the next attempt fix?"
            disabled={busy}
            project={detail.ticket.projectSlug}
            onChange={setSendNote}
          />
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
            <label className="drawer-comment-label" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              Destination
              <select
                className="input"
                value={sendTo}
                onChange={(event) => {
                  const next = event.target.value === 'Refining' ? 'Refining' : 'Implementing';
                  setSendTo(next);
                  setSendActivity('');
                }}
              >
                <option value="Implementing">Implementing</option>
                <option value="Refining">Refining</option>
              </select>
            </label>
            <label className="drawer-comment-label" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              Activity
              <select
                className="input"
                value={sendActivity}
                onChange={(event) => setSendActivity(event.target.value)}
              >
                <option value="">Default</option>
                {sendTo === 'Refining' ? (
                  <>
                    <option value="Standard">Standard</option>
                    <option value="Research">Research</option>
                    <option value="Planning">Planning</option>
                  </>
                ) : (
                  <option value="Merge or Rebase">Merge or Rebase</option>
                )}
              </select>
            </label>
          </div>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-accent"
              disabled={busy || sendNote.trim().length === 0}
              onClick={() =>
                void run(`Sent back to ${sendTo}`, () =>
                  api.sendBack(key, sendTo, {
                    expectedVersion: version,
                    note: sendNote.trim(),
                    ...(sendActivity ? { activity: sendActivity } : {}),
                  }),
                )
              }
            >
              Send back
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setMode('idle')}>
              Keep it
            </button>
          </div>
        </div>
      ) : null}

      {mode === 'wait' ? (
        <div className="rise drawer-wait" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label className="drawer-comment-label" htmlFor="wait-reason-type">
            Waiting reason
          </label>
          <select
            id="wait-reason-type"
            className="input"
            value={waitReasonType}
            onChange={(event) => setWaitReasonType(event.target.value)}
          >
            <option value="manual_hold">Manual hold</option>
            <option value="human_clarification">Clarification</option>
          </select>
          <MarkdownComposer
            id="wait-reason"
            value={waitReason}
            rows={3}
            placeholder="Why is this parked?"
            disabled={busy}
            project={detail.ticket.projectSlug}
            onChange={setWaitReason}
          />
          <div style={{ display: 'flex', gap: 7 }}>
            <button
              type="button"
              className="btn btn-accent"
              disabled={busy || waitReason.trim().length === 0}
              onClick={() =>
                void run('Parked', () =>
                  api.wait(key, {
                    reason_type: waitReasonType,
                    reason: waitReason.trim(),
                    expectedVersion: version,
                  }),
                )
              }
            >
              Move to Waiting
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setMode('idle')}>
              Keep it
            </button>
          </div>
        </div>
      ) : null}

      {mode === 'split' ? (
        <div className="rise" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <input className="input" value={childKey} placeholder="Child key" onChange={(event) => setChildKey(event.target.value)} />
          <input className="input" value={childTitle} placeholder="Child title" onChange={(event) => setChildTitle(event.target.value)} />
          <div style={{ display: 'flex', gap: 7 }}>
            <button
              type="button"
              className="btn btn-accent"
              disabled={busy || childKey.trim().length === 0 || childTitle.trim().length === 0}
              onClick={() =>
                void run('Split', () =>
                  api.split(key, [{ ticket_key: childKey.trim(), title: childTitle.trim() }], version),
                )
              }
            >
              Split
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setMode('idle')}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {mode === 'reset-budget' ? (
        <ConfirmRow
          busy={busy}
          tone="warn"
          body="Reset this ticket's repair budgets. Admin only."
          confirm="Reset budgets"
          onConfirm={() => void run('Budget reset', () => api.resetBudget(key, { expectedVersion: version }))}
          onCancel={() => setMode('idle')}
        />
      ) : null}

      {mode === 'cancel' ? (
        <div
          className="rise drawer-cancel-confirm"
          style={{
            padding: '11px 13px',
            borderRadius: 'var(--r2)',
            boxShadow: 'inset 0 0 0 1px var(--danger-edge)',
            display: 'flex',
            flexDirection: 'column',
            gap: 9,
          }}
        >
          <p style={{ color: 'var(--text-2)', lineHeight: 1.6 }}>
            Cancelling {key} closes the GitLab merge request, preserves the branch, and stops
            in-flight attempts. Restore reopens the MR (or opens a fresh draft MR on the same
            branch) in Backlog. Type{' '}
            <span className="mono" style={{ color: 'var(--danger-text)' }}>
              {key}
            </span>{' '}
            to confirm.
          </p>
          <div style={{ display: 'flex', gap: 7 }}>
            <input
              className="input"
              style={{ maxWidth: 160 }}
              value={confirmKey}
              autoFocus
              placeholder={key}
              onChange={(event) => setConfirmKey(event.target.value)}
            />
            <button
              type="button"
              className="btn btn-danger"
              disabled={busy || confirmKey.trim().toUpperCase() !== key.toUpperCase()}
              onClick={() => void run('Cancelled', () => api.cancel(key, confirmKey, version))}
            >
              Cancel {key}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setMode('idle')}>
              Keep it
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ConfirmRow({
  busy,
  tone,
  body,
  confirm,
  onConfirm,
  onCancel,
}: {
  busy: boolean;
  tone: 'merge' | 'warn';
  body: string;
  confirm: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      className={tone === 'merge' ? 'rise drawer-merge-confirm' : 'rise'}
      style={{
        padding: '11px 13px',
        borderRadius: 'var(--r2)',
        background: tone === 'merge' ? 'rgba(236,72,153,0.09)' : 'rgba(255,180,36,0.08)',
        boxShadow: 'inset 0 0 0 1px var(--hairline)',
        display: 'flex',
        flexDirection: 'column',
        gap: 9,
      }}
    >
      <p style={{ lineHeight: 1.6, color: 'var(--text-2)' }}>{body}</p>
      <div style={{ display: 'flex', gap: 7 }}>
        <button type="button" className={tone === 'merge' ? 'btn btn-pink' : 'btn btn-accent'} disabled={busy} onClick={onConfirm}>
          {confirm}
        </button>
        <button type="button" className="btn btn-ghost" onClick={onCancel}>
          Keep it
        </button>
      </div>
    </div>
  );
}

/** True when a key event's target is the document itself rather than an element inside it. */
function isPageBody(target: EventTarget | null): boolean {
  return target === null || target === document || target === document.body || target === document.documentElement;
}
